import React, {useEffect, useState} from "react";
import {Button, Col, Form, ListGroup, Row, Spinner} from "react-bootstrap";
import {useCanvasDataSources} from "@/hooks/canvas";
import {EAIClient} from "Services/EAIClient";
import {useAppContext} from "@/contexts/app";
import {Utils} from "../../common/utils";
import {useSearchParams} from "react-router-dom";
import PageTitle from "Components/Common/PageTitle";


export default function CanvasPage() {
    const {data: canvasDatasource, error, mutate} = useCanvasDataSources();
    const [clientId, setClientId] = useState("");
    const [clientKey, setClientKey] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const client = new EAIClient();
    const {dispatchError, addAlert} = useAppContext();

    const LEFT_COL_WIDTH = 3;
    const RIGHT_COL_WIDTH = 12 - LEFT_COL_WIDTH;

    const save = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSubmitting(true);
        const data = {
            settings: {
                client_id: clientId,
                client_secret: clientKey
            }
        };
        try {
            await client.updateCanvasDatasource(data);
            addAlert("Save Complete");
            await mutate();
        } catch (e) {
            dispatchError(e);
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        async function initialize() {
            Utils.setViewName("CanvasConfiguration", "Canvas");

            if (searchParams.get("error")) {
                let errorText = searchParams.get("error") as string;
                if (searchParams.get("error_description")) {
                    errorText = searchParams.get("error_description") as string;
                }
                dispatchError(new Error("Canvas Error"), errorText);
            } else if (searchParams.get("code")) {
                const data = {
                    settings: {
                        code: searchParams.get("code") as string
                    }
                };
                try {
                    await client.updateCanvasDatasource(data);
                    addAlert("Canvas Configuration Complete");
                    searchParams.delete("code");
                    setSearchParams(searchParams.toString());
                    await mutate();
                } catch (e) {
                    dispatchError(e);
                }
            }
        }
        initialize();
    }, []);

    useEffect(() => {
        if (canvasDatasource) {
            setClientId(canvasDatasource.data.settings.client_id || "");
            setClientKey(canvasDatasource.data.settings.client_secret || "");
        }
    }, [canvasDatasource]);

    if (error) {
        return <div className="p-3 text-center">Error loading data: {error.message}</div>;
    }

    if (!canvasDatasource) {
        return <div className="p-3 text-center"><Spinner variant="success" /></div>;
    }

    return (
        <>
            <PageTitle pageTitle={"Canvas Configuration"} />
            <Row className="section-action-bar">
                <Col md={12} lg={6} className="title">
                    <h3>Canvas Configuration</h3>
                </Col>
            </Row>
            <div>
                <div className="row-padding-bottom">
                    <h3>Step 1:</h3>
                    <a target="_blank" rel="noopener noreferrer"
                       href="https://knowledgebase.enflux.com/en/articles/5530618-how-do-i-give-enflux-access-to-canvas">Create
                        a Canvas User</a>
                </div>

                <div className="row-padding-bottom">
                    <h3>Step 2:</h3>
                    <span>
                            Copy the following to the Canvas Developer Key settings (<a target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        href="http://knowledgebase.enflux.com/en/articles/5964449-canvas-create-api-developer-key">How do I add a developer key?</a>):
                            <Row>
                                <Col md={LEFT_COL_WIDTH}>
                                    <strong>Key Name:</strong>
                                </Col>
                                <Col md={RIGHT_COL_WIDTH}>
                                    Enflux
                                </Col>
                            </Row>
                            <Row>
                                <Col md={LEFT_COL_WIDTH}>
                                    <strong>Owner Email:</strong>
                                </Col>
                                <Col md={RIGHT_COL_WIDTH}>
                                    support@enflux.com
                                </Col>
                            </Row>
                            <Row>
                                <Col md={LEFT_COL_WIDTH}>
                                    <strong>Redirect URIs:</strong>
                                </Col>
                                <Col md={RIGHT_COL_WIDTH}>
                                    {canvasDatasource.data.settings.redirect_uris}
                                </Col>
                            </Row>
                            <Row>
                                <Col md={LEFT_COL_WIDTH}>
                                    <strong>Redirect URI (Legacy):</strong>
                                </Col>
                                <Col md={RIGHT_COL_WIDTH}>
                                    {canvasDatasource.data.settings.redirect_uri_legacy}
                                </Col>
                            </Row>
                            <Row>
                                <Col md={LEFT_COL_WIDTH}>
                                    <strong>Vendor Code (LTI 2):</strong>
                                </Col>
                                <Col md={RIGHT_COL_WIDTH}>
                                    Leave Blank
                                </Col>
                            </Row>
                            <Row>
                                <Col md={LEFT_COL_WIDTH}>
                                    <strong>Icon URL:</strong>
                                </Col>
                                <Col md={RIGHT_COL_WIDTH}>
                                    {canvasDatasource.data.settings.icon_url}
                                </Col>
                            </Row>
                            <Row>
                                <Col md={LEFT_COL_WIDTH}>
                                    <strong>Client Credentials Audience:</strong>
                                </Col>
                                <Col md={RIGHT_COL_WIDTH}>
                                    Canvas
                                </Col>
                            </Row>
                        </span>
                    <Row className="row-padding-top">
                        <Col>
                            <h5>Optional</h5> <a target="_blank" rel="noopener noreferrer"
                                                 href="https://knowledgebase.enflux.com/en/articles/5998763-enabling-scope-on-canvas-developer-keys-optional">Enable
                            Scope on Keys</a>
                        </Col>
                    </Row>
                </div>
                <div className="row-padding-bottom">
                    <h3>Step 3:</h3>
                    <Row className="row-padding-bottom">
                        <Col>
                            Copy the Client ID (it will look like <em>157060000000000201</em>) and Key (click &quot;Show
                            Key&quot;, it will look
                            like <em>AKdIRM4ISlczp5cDzB2ZUlxN56uKSGqeF0xF20sbiteJT9xHA7UcIN5IsLnTibVe</em>) from
                            Canvas to the fields below.<br/>
                        </Col>
                    </Row>
                    <Form onSubmit={save}>
                        <Form.Group controlId="formClientID">
                            <Form.Label column="lg">Client ID</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter Client ID"
                                onChange={(ev) => setClientId(ev.target.value)}
                                value={clientId}
                                defaultValue={canvasDatasource.data.settings.client_id}
                                disabled={canvasDatasource.data.settings.ui_locked === "true"}
                            />
                        </Form.Group>
                        <Form.Group controlId="formClientKey" className="mb-2">
                            <Form.Label column="lg">Client Key</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter Client Key"
                                onChange={ev=>setClientKey(ev.target.value)}
                                value={clientKey}
                                defaultValue={canvasDatasource.data.settings.client_secret}
                                disabled={canvasDatasource.data.settings.ui_locked === "true"}
                            />
                        </Form.Group>
                        {submitting
                            ? <Spinner animation="border" size="sm"/>
                            : (
                                <p>
                                    <Button type="submit" disabled={canvasDatasource.data.settings.ui_locked === "true"}>Save</Button>
                                    {canvasDatasource.data.settings.ui_locked === "true" &&
                                        <span className="ms-2 text-warning">This configuration is locked. Please contact Enflux support to unlock.</span>}
                                </p>
                            )
                        }
                    </Form>
                </div>
                <div className="row-padding-bottom">
                    <h3>Step 4:</h3>
                    <span>
                            <ListGroup as="ol">
                                <ListGroup.Item as="li">Log out of Canvas</ListGroup.Item>
                                <ListGroup.Item
                                    as="li">Log in to the Enflux Canvas account created in Step 1</ListGroup.Item>
                                <ListGroup.Item as="li">
                                    Follow this URL and accept the request to give Enflux access:<br/>
                                    <a href={canvasDatasource.data.settings.authorize_url}>{canvasDatasource.data.settings.authorize_url}</a>
                                </ListGroup.Item>
                            </ListGroup>
                        </span>
                </div>
            </div>
        </>
    );
}
